import React, { useMemo } from "react";
import {
  MonitorAlert,
  MonitorMode,
  MonitorNotice,
  MonitorStats,
  formatClock,
  pickPrimaryAlert,
} from "./emailsMonitorUtils";
import "./styles/EmailsMonitorSidePanel.css";

type Props = {
  mode: MonitorMode;
  stats: MonitorStats;
  alerts: MonitorAlert[];
  notices: MonitorNotice[];
  dayLabel: string;
  lastUpdatedAt?: Date | null;
  live?: boolean;
  loading?: boolean;
  collapsed?: boolean;
  onModeChange: (mode: MonitorMode) => void;
  onFocusMissingService: () => void;
  onFocusMissingDriver: () => void;
  onOpenNotice?: (notice: MonitorNotice) => void;
  onRefresh?: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
};

const EmailsMonitorSidePanel: React.FC<Props> = ({
  mode,
  stats,
  alerts,
  notices,
  dayLabel,
  lastUpdatedAt = null,
  live = false,
  loading = false,
  collapsed = false,
  onModeChange,
  onFocusMissingService,
  onFocusMissingDriver,
  onOpenNotice,
  onRefresh,
  onCollapsedChange,
}) => {
  const primary = useMemo(() => pickPrimaryAlert(alerts), [alerts]);
  const primaryAction =
    primary.id === "missing-driver"
      ? onFocusMissingDriver
      : primary.id === "missing-service"
        ? onFocusMissingService
        : undefined;

  if (collapsed) {
    return (
      <aside
        className="emails-monitor-panel emails-monitor-panel--collapsed"
        aria-label="Panel de monitoreo de correos (oculto)"
      >
        <button
          type="button"
          className="emails-monitor-panel-expand"
          onClick={() => onCollapsedChange?.(false)}
          title="Mostrar control operativo"
          aria-expanded={false}
        >
          <span className={`emails-monitor-panel-dot${live ? " is-live" : ""}`} aria-hidden />
          <span className="emails-monitor-panel-expand-label">Control operativo</span>
          <span className="emails-monitor-panel-expand-chevron" aria-hidden>
            «
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="emails-monitor-panel" aria-label="Panel de monitoreo de correos">
      <header className="emails-monitor-panel-header">
        <div className="emails-monitor-panel-live">
          <span className={`emails-monitor-panel-dot${live ? " is-live" : ""}`} aria-hidden />
          <div>
            <strong>Control operativo</strong>
            <span>Actualizado {formatClock(lastUpdatedAt)}</span>
          </div>
        </div>
        <div className="emails-monitor-panel-header-actions">
          {onRefresh && (
            <button
              type="button"
              className="emails-monitor-panel-refresh"
              onClick={onRefresh}
              disabled={loading}
              title="Actualizar"
            >
              {loading ? "…" : "↻"}
            </button>
          )}
          {onCollapsedChange && (
            <button
              type="button"
              className="emails-monitor-panel-collapse"
              onClick={() => onCollapsedChange(true)}
              title="Ocultar control operativo"
              aria-expanded={true}
              aria-label="Ocultar control operativo"
            >
              »
            </button>
          )}
        </div>
      </header>

      <div className="emails-monitor-scope" role="status">
        <span className="emails-monitor-scope-kicker">Resumen del día</span>
        <strong>{dayLabel}</strong>
        <span>Mismo día que correos / solicitudes</span>
      </div>

      <section
        className={`emails-monitor-primary emails-monitor-primary--${primary.tone}`}
        aria-live="polite"
      >
        <span className="emails-monitor-primary-kicker">Lo más relevante · {dayLabel}</span>
        <strong>{primary.title}</strong>
        <p>{primary.detail}</p>
        {primaryAction ? (
          <button type="button" className="emails-monitor-primary-action" onClick={primaryAction}>
            Ver en listado
          </button>
        ) : null}
      </section>

      <section className="emails-monitor-panel-modes" aria-label="Modos de monitoreo">
        <button
          type="button"
          className={`emails-monitor-mode${mode === "servicio" ? " is-active" : ""}`}
          aria-pressed={mode === "servicio"}
          onClick={() => onModeChange("servicio")}
        >
          <span className="emails-monitor-mode-kicker">Modo 1</span>
          <strong>Servicio agendado</strong>
          <span>¿La solicitud del día ya tiene servicio?</span>
        </button>
        <button
          type="button"
          className={`emails-monitor-mode${mode === "conductor" ? " is-active" : ""}`}
          aria-pressed={mode === "conductor"}
          onClick={() => onModeChange("conductor")}
        >
          <span className="emails-monitor-mode-kicker">Modo 2</span>
          <strong>Conductor</strong>
          <span>¿El servicio agendado tiene conductor?</span>
        </button>
      </section>

      <section className="emails-monitor-panel-metrics" aria-label="Métricas del día">
        <div className="emails-monitor-metric-card">
          <span>Día · {dayLabel}</span>
          <strong>{stats.scopeCount}</strong>
          <em>en listado</em>
        </div>
        <button
          type="button"
          className="emails-monitor-metric-card emails-monitor-metric-card--danger"
          onClick={onFocusMissingService}
        >
          <span>Sin servicio</span>
          <strong>{stats.scopeSinServicio}</strong>
          <em>por agendar</em>
        </button>
        <button
          type="button"
          className="emails-monitor-metric-card emails-monitor-metric-card--warning"
          onClick={onFocusMissingDriver}
        >
          <span>Sin conductor</span>
          <strong>{stats.scopeSinConductor}</strong>
          <em>con servicio</em>
        </button>
        <div className="emails-monitor-metric-card emails-monitor-metric-card--ok">
          <span>Listas</span>
          <strong>{stats.listo}</strong>
          <em>operativas</em>
        </div>
      </section>

      <section className="emails-monitor-panel-activity">
        <h3>Últimas notificaciones</h3>
        {notices.length === 0 ? (
          <p className="emails-monitor-panel-empty">
            Aún no hay notificaciones guardadas del sync.
          </p>
        ) : (
          <ul>
            {notices.map((item) => {
              const canOpen = Boolean(item.emailId || item.serviceId);
              return (
                <li
                  key={item.id}
                  className={`emails-monitor-activity emails-monitor-activity--${item.tone}`}
                >
                  <button
                    type="button"
                    className="emails-monitor-activity-btn"
                    disabled={!canOpen || !onOpenNotice}
                    onClick={() => onOpenNotice?.(item)}
                  >
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.detail}</span>
                      {item.source === "sync" ? (
                        <em className="emails-monitor-notice-source">Sync guardada</em>
                      ) : null}
                    </div>
                    <time dateTime={item.at}>{item.when}</time>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </aside>
  );
};

export default React.memo(EmailsMonitorSidePanel);
